import { useMemo, useState } from 'react';
import { SmilePlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import {
  Popover,
  PopoverTrigger,
} from '@/components/ui/popover';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { MessageReaction } from '@/hooks/useMessageReactions';

const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '🎉', '🔥'];

interface MessageReactionsProps {
  messageId: string;
  reactions: MessageReaction[];
  onToggle: (messageId: string, emoji: string) => void;
  /** Aligns the add-reaction button to the side opposite the bubble's tail. */
  alignEnd?: boolean;
}

export function MessageReactions({
  messageId,
  reactions,
  onToggle,
  alignEnd,
}: MessageReactionsProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  // Group by emoji
  const grouped = useMemo(() => {
    const map = new Map<string, { count: number; mine: boolean }>();
    for (const r of reactions) {
      const cur = map.get(r.emoji) ?? { count: 0, mine: false };
      cur.count += 1;
      if (user && r.user_id === user.id) cur.mine = true;
      map.set(r.emoji, cur);
    }
    return Array.from(map.entries());
  }, [reactions, user]);

  const handlePick = (emoji: string) => {
    setOpen(false);
    onToggle(messageId, emoji);
  };

  if (grouped.length === 0) {
    // Hover-only add button when no reactions yet
    return (
      <div
        className={cn(
          'flex mt-1',
          alignEnd ? 'justify-end' : 'justify-start'
        )}
      >
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Add reaction"
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity h-6 w-6 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground flex items-center justify-center"
            >
              <SmilePlus className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverPrimitive.Content
            side="top"
            align={alignEnd ? 'end' : 'start'}
            sideOffset={4}
            className="w-auto p-1.5 rounded-full shadow-xl border bg-popover z-[200]"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <EmojiRow onPick={handlePick} />
          </PopoverPrimitive.Content>
        </Popover>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1 mt-1',
        alignEnd ? 'justify-end' : 'justify-start'
      )}
    >
      {grouped.map(([emoji, { count, mine }]) => (
        <button
          key={emoji}
          type="button"
          onClick={() => onToggle(messageId, emoji)}
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs border transition-colors',
            mine
              ? 'bg-primary/15 border-primary/40 text-foreground'
              : 'bg-muted border-transparent text-muted-foreground hover:bg-muted/80'
          )}
          aria-label={`${emoji} ${count}${mine ? ' (you reacted)' : ''}`}
        >
          <span aria-hidden className="text-sm leading-none">{emoji}</span>
          <span className="tabular-nums">{count}</span>
        </button>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Add reaction"
            className="h-6 w-6 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground flex items-center justify-center opacity-60 hover:opacity-100"
          >
            <SmilePlus className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverPrimitive.Content
          side="top"
          align={alignEnd ? 'end' : 'start'}
          sideOffset={4}
          className="w-auto p-1.5 rounded-full shadow-xl border bg-popover z-[200]"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <EmojiRow onPick={handlePick} />
        </PopoverPrimitive.Content>
      </Popover>
    </div>
  );
}

function EmojiRow({ onPick }: { onPick: (emoji: string) => void }) {
  return (
    <div className="flex items-center gap-0.5">
      {QUICK_EMOJIS.map((emoji) => (
        <button
          key={emoji}
          type="button"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onPick(emoji);
          }}
          className={cn(
            'text-xl leading-none rounded-full p-1.5 transition-transform duration-150 ease-out',
            'hover:scale-125 hover:-translate-y-0.5 active:scale-110'
          )}
        >
          <span aria-hidden>{emoji}</span>
        </button>
      ))}
    </div>
  );
}